import { api } from "@api/api";
import { apiClient } from "@lib/axios";
import { reactQueryUtils } from "@lib/react-query";
import { toast } from "react-toastify";
import { signOut } from "@/features/authentication";
import { COLLABORATION_COLORS } from "@/shared/constants";
import { setCookie } from "@/shared/utils/cookies";
import type { TUser } from "./users.types";

const path = (path: string) => `/v2/users${path}`;

export const usersApi = {
  getUsers: () => {
    return apiClient.get<TUser[]>(path(""));
  },

  getUsersCount: () => {
    return apiClient.get<{ count: number }>(path("/count"));
  },

  getCurrentUser: async () => {
    const maxRetries = 3;
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiClient.get<TUser>(path("/me"));
      } catch (error) {
        lastError = error;
        if (attempt === maxRetries) {
          // After 3 failed attempts, logout the user
          toast.error("Failed to login. Please contact support.");
          try {
            await signOut();
            setTimeout(() => {
              reactQueryUtils.clear();
              api.util.resetApiState();
              window.location.href = "/login";
            }, 1500);
          } catch (logoutError) {
            console.error(
              "Failed to logout user after getCurrentUser failure:",
              logoutError,
            );
          }
          throw lastError;
        }
        // Wait a bit before retrying (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, 2 ** attempt * 100));
      }
    }

    throw lastError;
  },

  deleteUser: (oktaId: string) => {
    return apiClient.delete(path(`/${oktaId}`));
  },

  reactivateUser: (oktaId: string) => {
    return apiClient.post(path(`/${oktaId}/reactivateUser`), {});
  },

  changeRole: (oktaId: string, newRole: string) => {
    return apiClient.post(path(`/${oktaId}/changeRole`), { newRole });
  },

  updateUser: (oktaId: string, userData: { firstName: string }) => {
    return apiClient.post(path(`/${oktaId}`), userData);
  },

  updateUserFirstShare: (id: string, orgId: string) => {
    return apiClient.post(path(`/${id}/first-share`), { orgId });
  },

  updateUserCompleteProfile: (id: string, orgId: string) => {
    return apiClient.post(path(`/${id}/complete-profile`), { orgId });
  },

  updateUserProfile: (data: any) => {
    return apiClient.post(path("/update-profile"), data);
  },

  updateAutosaveCount: (counter: number) => {
    return apiClient.post(path("/update-autosave-counter"), { counter });
  },

  updateUserFirstViewEditor: () => {
    return apiClient.put(path("/first-view-editor"), {});
  },

  setWalkthrough: (walkthrough: {
    lastStepIndex: number;
    hasSkipped: boolean;
  }) => {
    return apiClient.post(path("/set-walkthrough"), { walkthrough });
  },

  updateFirstLogin: () => {
    return apiClient.post(path("/firstLogin"), {});
  },

  // Intercom Messenger identity verification: the server proves the user's
  // identity (keyed on the Firebase uid). Prefer `userJwt` (HS256, passed as
  // `intercom_user_jwt`); `userHash` is the deprecated HMAC kept for transition.
  getIntercomHash: () => {
    return apiClient.get<{ userHash: string; userJwt: string }>(
      path("/intercom-hash"),
    );
  },
};

export const transformUsersResponse = (response: TUser[]) => {
  return response.map((user) => {
    const { color, theme } = user?.profile || {};
    const userTheme =
      color && theme
        ? { color, theme }
        : COLLABORATION_COLORS[
            Math.floor(Math.random() * COLLABORATION_COLORS.length)
          ];

    return {
      ...user,
      theme: userTheme,
    };
  });
};

export const handleCurrentUserResponse = (data: TUser) => {
  if (data?.oktaId) {
    setCookie({
      name: "storydoc_user_id",
      value: data.oktaId,
      days: 30,
    });
  }
  return data;
};
